// Copyright 2021 Pangea Cyber Corporation
// Author: Pangea Cyber Corporation

import sanitizeHtml from "sanitize-html";
import { sanitizeConfig } from "@src/urls";

export const UTM_FIELD_KEYS = {
  UTM_CAMPAIGN: "utm_campaign",
  UTM_SOURCE: "utm_source",
  UTM_TERM: "utm_term",
  UTM_MEDIUM: "utm_medium",
  UTM_CONTENT: "utm_content",
  GCLID: "gclid",
  REFERRER: "referrer",
  ONBOARDING: "onboarding",
};

// Keep these separate so we can process them as one set of data
export const UTM_FIELDS = [
  UTM_FIELD_KEYS.UTM_CAMPAIGN,
  UTM_FIELD_KEYS.UTM_SOURCE,
  UTM_FIELD_KEYS.UTM_TERM,
  UTM_FIELD_KEYS.UTM_MEDIUM,
  UTM_FIELD_KEYS.UTM_CONTENT,
];

// Additional fields which we in some cases we handle like UTM
// values (depends on business and marketing needs)
export const ADDITIONAL_UTM_FIELDS = [
  UTM_FIELD_KEYS.ONBOARDING,
  UTM_FIELD_KEYS.REFERRER,
  UTM_FIELD_KEYS.GCLID,
];

export const ALL_UTM_FIELDS = [...UTM_FIELDS, ...ADDITIONAL_UTM_FIELDS];

export type UTM_FIELD = (typeof ALL_UTM_FIELDS)[number];

export type UtmParameters = {
  [key: UTM_FIELD]: string;
};

export const UTM_COOKIE_NAME = "pangea-utm-code";

/**
 * Check if our input values have any UTMs filled in
 *
 * @param inputUtms - An object which may have UTMs
 * @returns boolean
 */
const areUtmsSet = (inputUtms: any = {}) => {
  for (const value of UTM_FIELDS) {
    if (inputUtms?.[value]) {
      return true;
    }
  }

  return false;
};

/**
 * Calculate which UTMs should be used. We prefer UTMs in this order:
 * - URL Query Parameters
 * - (If set) Page UTMs
 * - UTMs which are cookied
 *
 * If a user has cookied UTMs _and_ UTMs in the query string, we will only use
 * the UTMs in the query string because they are more current.
 *
 * Note for future devs: PageUTMs should only be passed for static sign up forms (home page hero get started for example) and should not
 * be passed on hubspot form pages
 */
export const calculateUtms = (pageUtms?: UtmParameters): UtmParameters => {
  const newUtmData: UtmParameters = {};

  if (typeof window !== "undefined") {
    const parameterUtms = new URLSearchParams(window.location.search);
    const cookieUtms = getUTMCodesFromCookie();

    // Grouped UTM values
    if (areUtmsSet(Object.fromEntries(parameterUtms))) {
      for (const utmKey of UTM_FIELDS) {
        if (
          parameterUtms.has(utmKey) &&
          parameterUtms.get(utmKey) !== null &&
          parameterUtms.get(utmKey)?.trim() !== ""
        ) {
          newUtmData[utmKey] = sanitizeHtml(
            parameterUtms.get(utmKey) ?? "",
            sanitizeConfig,
          );
        }
      }
    } else if (pageUtms && areUtmsSet(pageUtms)) {
      for (const utmKey of UTM_FIELDS) {
        if (
          pageUtms?.[utmKey] &&
          typeof pageUtms?.[utmKey] === "string" &&
          pageUtms?.[utmKey]?.trim() !== ""
        ) {
          newUtmData[utmKey] = sanitizeHtml(
            pageUtms[utmKey] ?? "",
            sanitizeConfig,
          );
        }
      }
    } else if (areUtmsSet(cookieUtms)) {
      for (const utmKey of UTM_FIELDS) {
        if (
          cookieUtms?.[utmKey] &&
          typeof cookieUtms?.[utmKey] === "string" &&
          cookieUtms?.[utmKey]?.trim() !== ""
        ) {
          newUtmData[utmKey] = sanitizeHtml(
            cookieUtms[utmKey] ?? "",
            sanitizeConfig,
          );
        }
      }
    }

    // Individual values
    for (const fieldKey of ADDITIONAL_UTM_FIELDS) {
      if (
        parameterUtms.has(fieldKey) &&
        parameterUtms.get(fieldKey) !== null &&
        parameterUtms.get(fieldKey)?.trim() !== ""
      ) {
        newUtmData[fieldKey] = sanitizeHtml(
          parameterUtms.get(fieldKey) ?? "",
          sanitizeConfig,
        );
      } else if (
        pageUtms &&
        pageUtms?.[fieldKey] &&
        typeof pageUtms?.[fieldKey] === "string" &&
        pageUtms?.[fieldKey]?.trim() !== ""
      ) {
        newUtmData[fieldKey] = sanitizeHtml(
          pageUtms[fieldKey] ?? "",
          sanitizeConfig,
        );
      } else if (
        cookieUtms?.[fieldKey] &&
        typeof cookieUtms?.[fieldKey] === "string" &&
        cookieUtms?.[fieldKey]?.trim() !== ""
      ) {
        newUtmData[fieldKey] = sanitizeHtml(
          cookieUtms[fieldKey] ?? "",
          sanitizeConfig,
        );
      }
    }
  }

  return newUtmData;
};

/**
 * If there are UTM values in the cookie we will return them
 */
export function getUTMCodesFromCookie(): Record<string, string> {
  let pangeaUTM = document?.cookie
    ?.split(";")
    .find((cookie) => cookie.trim().startsWith(UTM_COOKIE_NAME));

  const utmParams = new URLSearchParams(
    pangeaUTM?.split(`${UTM_COOKIE_NAME}=`)[1],
  );

  const UTMCodesObj: Record<string, string> = {};

  utmParams.forEach((value, key) => {
    UTMCodesObj[key] = value;
  });

  return UTMCodesObj;
}

/**
 * Used to apply safely apply UTM values to the hidden UTM
 * (and possibly other tracking like GCLID or referrer) fields
 * in our hubspot forms.
 *
 * The expected behavior is if the field does not exist, we
 * ignore it, since fields are managed outside of the code
 */
export function applyUtmsToHubspotForm($form: HTMLFormElement) {
  if ($form) {
    try {
      const newUtmData = calculateUtms();

      for (const utmKey of ALL_UTM_FIELDS) {
        const utmElement = $form?.elements?.namedItem(
          utmKey,
        ) as HTMLInputElement;

        if (utmElement && newUtmData[utmKey]) {
          utmElement.value = newUtmData[utmKey];
        }
      }
    } catch (_ignore) {}
  }
}
